/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Icons, Positions } from '@sede-enterprise/shell-ds-react-framework';
import type { ISelectProps } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Select/Select.types';
import { FieldValues, FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { Modal } from '../../NominationGroups/style';
import { Col, FormRow } from 'src/UI/Grid';
import Button from 'src/UI/Button';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import FormControlToggle from 'src/UI/FormComponent/FormControlToggle';
import { get } from 'src/services/GenesisApi';
import { getParameterDefinitionsUrl, MESSAGES, ParameterScope } from 'src/constants/EUMarkets';
import { handleError } from 'src/utils/common/utils';
import { ISelectOptions } from 'src/types/Common.type';
import { INGParameters, IParameterDefinition } from 'src/types/EUMarkets.type';
import ErrorMessage from 'src/UI/ErrorMessage';

const { Trash } = Icons;

interface IAddEditLocationParameter {
  open: boolean;
  inline?: boolean;
  onClose: () => void;
  scopeId: ParameterScope;
  locationId: number;
  parameter: INGParameters | null;
  onSave?: (parameter: INGParameters) => void;
  onDraftChange?: (parameter: INGParameters | null, error?: string) => void;
  showHeader?: boolean;
  title?: string;
  formError?: string;
  definitions?: IParameterDefinition[];
  loadingDefinitions?: boolean;
}

type DateValue = string | Dayjs;
type OptionalDateValue = DateValue | null;

export const PARAMETER_DATA_TYPE_IDS = {
  String: 1,
  Long: 2,
  Double: 3,
  Boolean: 4,
  List: 5,
} as const;

const VALUE_KEY_BY_DATA_TYPE_ID: Record<number, string> = {
  [PARAMETER_DATA_TYPE_IDS.String]: 'valueString',
  [PARAMETER_DATA_TYPE_IDS.Long]: 'valueLong',
  [PARAMETER_DATA_TYPE_IDS.Double]: 'valueDouble',
  [PARAMETER_DATA_TYPE_IDS.Boolean]: 'valueBoolean',
  [PARAMETER_DATA_TYPE_IDS.List]: 'valueList',
};

export const getValueKey = (dataType: string) => {
  const normalizedType = String(dataType ?? '').toLowerCase();
  if (normalizedType.includes('bool')) {
    return 'valueBoolean';
  }
  if (normalizedType.includes('list')) {
    return 'valueList';
  }
  if (normalizedType.includes('double') || normalizedType.includes('decimal')) {
    return 'valueDouble';
  }
  if (normalizedType.includes('long') || normalizedType.includes('int')) {
    return 'valueLong';
  }
  return 'valueString';
};

export const getDefinitionValueKey = (definition?: IParameterDefinition | null) =>
  VALUE_KEY_BY_DATA_TYPE_ID[definition?.dataTypeId ?? 0] ?? getValueKey(definition?.dataType ?? '');

export const getListOptions = (definition?: IParameterDefinition | null): ISelectOptions[] =>
  (definition?.listValues ?? []).map((listValue) => ({
    id: listValue.id,
    name: listValue.value,
  }));

export const getValueValidation = (valueKey: string) => {
  if (valueKey === 'valueLong') {
    return {
      required: MESSAGES.REQUIRED_PARAMETER_VALUE,
      validate: (value) => Number.isSafeInteger(Number(value)) || MESSAGES.INVALID_WHOLE_NUMBER,
    };
  }
  if (valueKey === 'valueDouble') {
    return {
      required: MESSAGES.REQUIRED_PARAMETER_VALUE,
      validate: (value) => Number.isFinite(Number(value)) || MESSAGES.INVALID_DECIMAL_NUMBER,
    };
  }
  if (valueKey === 'valueList') {
    return { required: MESSAGES.REQUIRED_PARAMETER_VALUE };
  }
  if (valueKey === 'valueBoolean') {
    return {};
  }
  return {
    required: MESSAGES.REQUIRED_PARAMETER_VALUE,
    validate: (value) => String(value).trim().length > 0 || MESSAGES.REQUIRED_PARAMETER_VALUE,
  };
};

export const getParameterValue = (parameter: INGParameters | null) => {
  if (!parameter) {
    return '';
  }
  if (parameter.valueListId != null) {
    return parameter.valueListId;
  }
  const value = [
    parameter.valueString,
    parameter.valueLong,
    parameter.valueDouble,
    parameter.valueBoolean,
  ].find((item) => item !== null && item !== undefined);
  return value ?? '';
};

// Builds the value columns for a parameter payload; only the column matching its type is populated.
export const toParameterValueFields = (
  valueKey: string,
  value: unknown,
  listOptions: ISelectOptions[],
) => {
  const stringValue = typeof value === 'string' ? value : '';
  return {
    valueString: valueKey === 'valueString' ? String(stringValue) : null,
    valueLong: valueKey === 'valueLong' ? Number(value) : null,
    valueDouble: valueKey === 'valueDouble' ? Number(value) : null,
    valueBoolean: valueKey === 'valueBoolean' ? Boolean(value) : null,
    valueListId: valueKey === 'valueList' ? Number(value) : null,
    valueListValue:
      valueKey === 'valueList'
        ? (listOptions.find((option) => String(option.id) === String(value))?.name ?? null)
        : null,
  };
};

export const isDateRangeValid = (validFrom?: OptionalDateValue, validTo?: OptionalDateValue) => {
  if (!validFrom || !validTo) {
    return true;
  }

  const from = dayjs(validFrom);
  const to = dayjs(validTo);

  if (!from.isValid() || !to.isValid()) {
    return true;
  }

  return !from.isAfter(to, 'day');
};

export const formatDateOnly = (value: Dayjs) =>
  typeof value.format === 'function'
    ? value.format('YYYY-MM-DD')
    : value.toISOString().slice(0, 10);

const toPositiveParameterDefinitionId = (value: unknown): number | undefined => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : undefined;
};

export const resolveParameterDefinitionId = (
  parameterName: string | undefined,
  definitions: IParameterDefinition[],
) => {
  const normalizedName = parameterName?.trim().toLowerCase();
  if (!normalizedName) {
    return undefined;
  }
  const matchingName = definitions.find(
    (definition) => definition.parameterName.trim().toLowerCase() === normalizedName,
  );
  if (matchingName) {
    return toPositiveParameterDefinitionId(matchingName.id);
  }

  const matchingAliases = definitions.filter(
    (definition) => definition.alias?.trim().toLowerCase() === normalizedName,
  );
  return matchingAliases.length === 1
    ? toPositiveParameterDefinitionId(matchingAliases[0].id)
    : undefined;
};

// Location/organization APIs return parameters without their definition id, so resolve it by name.
export const withResolvedDefinitionIds = (
  parameters: INGParameters[],
  definitions: IParameterDefinition[],
): INGParameters[] =>
  parameters.map((parameter) => ({
    ...parameter,
    parameterDefinitionId:
      toPositiveParameterDefinitionId(parameter.parameterDefinitionId) ??
      resolveParameterDefinitionId(parameter.parameterName, definitions) ??
      0,
  }));

export const areParameterDatesWithinParentRange = (
  parameter: INGParameters,
  parentValidFrom: Dayjs | undefined,
  parentValidTo: Dayjs | null,
) => {
  if (!parentValidFrom?.isValid()) {
    return true;
  }

  const parameterValidFrom = dayjs(parameter.validFrom);
  if (!parameterValidFrom.isValid() || parameterValidFrom.isBefore(parentValidFrom, 'day')) {
    return false;
  }

  if (!parentValidTo?.isValid()) {
    return true;
  }

  const parameterValidTo = parameter.validTo ? dayjs(parameter.validTo) : null;
  return Boolean(parameterValidTo?.isValid() && !parameterValidTo.isAfter(parentValidTo, 'day'));
};

export const hasOverlappingParameterDates = (parameters: INGParameters[]) => {
  const groupedParameters = parameters.reduce<Record<string, INGParameters[]>>(
    (groups, parameter) => {
      const groupKey = `${parameter.parameterDefinitionId}::${parameter.parameterName}`;
      groups[groupKey] = [...(groups[groupKey] || []), parameter];
      return groups;
    },
    {},
  );

  return Object.values(groupedParameters).some((group) => {
    const ranges = group
      .map((parameter) => ({
        from: dayjs(parameter.validFrom),
        to: parameter.validTo ? dayjs(parameter.validTo) : null,
      }))
      .sort((left, right) => left.from.valueOf() - right.from.valueOf());

    return ranges.slice(1).some((range, index) => {
      const previousRange = ranges[index];
      return !previousRange.to || !range.from.isAfter(previousRange.to, 'day');
    });
  });
};

const getDraftValidationError = (
  values: Record<string, unknown>,
  valueKey: string,
  isExistingParameter: boolean,
) => {
  const currentValue = values.value;
  if (!toPositiveParameterDefinitionId(values.parameterDefinitionId)) {
    return isExistingParameter
      ? MESSAGES.UNRESOLVED_PARAMETER_DEFINITION
      : MESSAGES.REQUIRED_PARAMETER_DEFINITION;
  }
  if (!values.validFrom) {
    return MESSAGES.REQUIRED_VALID_FROM;
  }
  if (!isDateRangeValid(values.validFrom as DateValue, values.validTo as OptionalDateValue)) {
    return MESSAGES.INVALID_PARAMETER_DATE_RANGE;
  }
  if (valueKey === 'valueLong' && !Number.isSafeInteger(Number(currentValue))) {
    return MESSAGES.INVALID_WHOLE_NUMBER;
  }
  if (valueKey === 'valueDouble' && !Number.isFinite(Number(currentValue))) {
    return MESSAGES.INVALID_DECIMAL_NUMBER;
  }
  if (
    (valueKey === 'valueString' || valueKey === 'valueList') &&
    (typeof currentValue !== 'string' || !currentValue.trim())
  ) {
    return MESSAGES.REQUIRED_PARAMETER_VALUE;
  }
  return '';
};

interface IParameterDefinitionControl {
  methods: UseFormReturn<FieldValues>;
  parameter: INGParameters | null;
  loadingDefinitions: boolean;
  definitionOptions: ISelectOptions[];
  label: string;
  showLabel?: boolean;
  placeholder?: string;
}

const ParameterDefinitionControl: FC<IParameterDefinitionControl> = ({
  methods,
  parameter,
  loadingDefinitions,
  definitionOptions,
  label,
  showLabel,
  placeholder,
}) => {
  if (parameter) {
    return (
      <FormControlTextInput
        methods={methods}
        label={label}
        showLabel={showLabel}
        controllerProps={{ name: 'parameterName' }}
        inputProps={{ disabled: true }}
      />
    );
  }

  return (
    <FormControlSelect
      methods={methods}
      label={label}
      showLabel={showLabel}
      isLoading={loadingDefinitions}
      data={definitionOptions}
      controllerProps={{
        name: 'parameterDefinitionId',
        rules: { required: MESSAGES.REQUIRED_PARAMETER_DEFINITION },
      }}
      selectProps={{
        ...(placeholder ? { placeholder } : {}),
        getPopupContainer: (triggerNode) =>
          triggerNode.closest('.parameter-edit-modal') ?? document.body,
        placement: (Positions?.Top ?? 'top') as ISelectProps['placement'],
      }}
    />
  );
};

interface IParameterValueControl {
  methods: UseFormReturn<FieldValues>;
  valueKey: string;
  listOptions: ISelectOptions[];
  showLabel?: boolean;
}

const ParameterValueControl: FC<IParameterValueControl> = ({
  methods,
  valueKey,
  listOptions,
  showLabel,
}) => {
  if (valueKey === 'valueBoolean') {
    return (
      <FormControlToggle
        methods={methods}
        label='Parameter Value'
        showLabel={showLabel}
        controllerProps={{ name: 'value' }}
        isBlock
      />
    );
  }

  if (valueKey === 'valueList') {
    return (
      <FormControlSelect
        methods={methods}
        label='Parameter Value'
        showLabel={showLabel}
        isLoading={false}
        data={listOptions}
        controllerProps={{
          name: 'value',
          rules: getValueValidation(valueKey),
        }}
        selectProps={{
          placeholder: 'Select Parameter Value',
          getPopupContainer: (triggerNode) =>
            triggerNode.closest('.parameter-edit-modal') ?? document.body,
          placement: (Positions?.Top ?? 'top') as ISelectProps['placement'],
        }}
      />
    );
  }

  return (
    <FormControlTextInput
      methods={methods}
      label='Parameter Value'
      showLabel={showLabel}
      controllerProps={{
        name: 'value',
        rules: getValueValidation(valueKey),
      }}
      inputProps={{
        type: valueKey === 'valueString' ? 'text' : 'number',
        step: valueKey === 'valueLong' ? '1' : 'any',
        placeholder: 'Enter Parameter Value',
      }}
    />
  );
};

const AddEditLocationParameter: FC<IAddEditLocationParameter> = ({
  open,
  inline = false,
  onClose,
  scopeId,
  locationId,
  parameter,
  onSave,
  onDraftChange,
  showHeader = false,
  title,
  formError,
  definitions: suppliedDefinitions,
  loadingDefinitions: suppliedLoadingDefinitions,
}) => {
  const methods = useForm();
  const { reset, setValue, watch } = methods;
  const [localLoadingDefinitions, setLocalLoadingDefinitions] = useState(false);
  const [localDefinitions, setLocalDefinitions] = useState<IParameterDefinition[]>([]);
  const definitions = suppliedDefinitions ?? localDefinitions;
  const loadingDefinitions = suppliedLoadingDefinitions ?? localLoadingDefinitions;
  const parameterDefinitionId = watch('parameterDefinitionId');
  const validFrom = watch('validFrom');
  const validTo = watch('validTo');
  // Existing rows show the name in a disabled input, so the id is resolved rather than selected.
  const resolvedDefinitionId =
    toPositiveParameterDefinitionId(parameter?.parameterDefinitionId) ||
    resolveParameterDefinitionId(parameter?.parameterName, definitions);
  const selectedDefinition = definitions.find(
    (definition) =>
      definition.id ===
      (toPositiveParameterDefinitionId(parameterDefinitionId) || resolvedDefinitionId),
  );
  const valueKey = getDefinitionValueKey(selectedDefinition);
  const listOptions = getListOptions(selectedDefinition);

  useEffect(() => {
    const canSubscribe = inline && open && onDraftChange && definitions.length > 0;

    if (!canSubscribe) {
      return undefined;
    }

    const subscription = methods.watch((values) => {
      const currentDefinitionId =
        toPositiveParameterDefinitionId(values.parameterDefinitionId) || resolvedDefinitionId || 0;
      const currentDefinition = definitions.find(
        (definition) => definition.id === currentDefinitionId,
      );
      const currentValueKey = getDefinitionValueKey(currentDefinition);
      onDraftChange(
        {
          id: parameter?.id ?? 0,
          locationId,
          parameterDefinitionId: currentDefinitionId,
          parameterName: currentDefinition?.parameterName ?? parameter?.parameterName ?? '',
          ...toParameterValueFields(
            currentValueKey,
            values.value,
            getListOptions(currentDefinition),
          ),
          validFrom: values.validFrom ? formatDateOnly(values.validFrom as Dayjs) : '',
          validTo: values.validTo ? formatDateOnly(values.validTo as Dayjs) : null,
        },
        getDraftValidationError(
          { ...values, parameterDefinitionId: currentDefinitionId },
          currentValueKey,
          Boolean(parameter),
        ),
      );
    });

    return () => subscription?.unsubscribe?.();
  }, [
    inline,
    open,
    parameter,
    locationId,
    definitions,
    valueKey,
    resolvedDefinitionId,
    onDraftChange,
  ]);

  useEffect(() => {
    if (!open || suppliedDefinitions !== undefined) {
      return;
    }
    setLocalLoadingDefinitions(true);
    get(getParameterDefinitionsUrl(scopeId))
      .then((response) => setLocalDefinitions(response.data || []))
      .catch((error) => handleError(error, 'Unable to load parameter definitions.'))
      .finally(() => setLocalLoadingDefinitions(false));
  }, [open, scopeId, suppliedDefinitions]);

  useEffect(() => {
    if (!open) {
      return;
    }
    reset({
      parameterDefinitionId: resolvedDefinitionId,
      parameterName: parameter?.parameterName ?? '',
      value: getParameterValue(parameter),
      validFrom: parameter?.validFrom ? dayjs(parameter.validFrom) : dayjs(),
      validTo: parameter?.validTo ? dayjs(parameter.validTo) : null,
    });
  }, [open, parameter, resolvedDefinitionId]);

  const definitionOptions: ISelectOptions[] = definitions
    .filter((definition) => definition.isActive !== false || definition.id === resolvedDefinitionId)
    .map((definition) => ({
      id: definition.id,
      name: definition.parameterName,
      description: definition.alias || definition.parameterName,
    }));

  const handleClose = () => onClose();

  const handleValidFromChange = (newValidFrom: Dayjs | null) => {
    if (newValidFrom && validTo && dayjs(newValidFrom).isAfter(dayjs(validTo), 'day')) {
      setValue('validTo', null, { shouldValidate: true });
    }
  };

  const handleSubmit = (formData) => {
    const selectedValueKey = getDefinitionValueKey(selectedDefinition);
    onSave?.({
      id: parameter?.id ?? 0,
      locationId,
      parameterDefinitionId: Number(formData.parameterDefinitionId) || resolvedDefinitionId || 0,
      parameterName: selectedDefinition?.parameterName ?? parameter?.parameterName ?? '',
      ...toParameterValueFields(selectedValueKey, formData.value, listOptions),
      validFrom: formatDateOnly(formData.validFrom as Dayjs),
      validTo: formData.validTo ? formatDateOnly(formData.validTo as Dayjs) : null,
    });
    handleClose();
  };

  if (inline && !open) {
    return null;
  }

  const formContent = (
    <FormProvider {...methods}>
      <FormRow>
        <Col size={12}>
          <ParameterDefinitionControl
            methods={methods}
            parameter={parameter}
            loadingDefinitions={loadingDefinitions}
            definitionOptions={definitionOptions}
            label='Parameter Definition'
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col size={12}>
          <ParameterValueControl methods={methods} valueKey={valueKey} listOptions={listOptions} />
        </Col>
      </FormRow>
      <FormRow>
        <Col size={6}>
          <FormControlDatePicker
            methods={methods}
            label='Valid From'
            controllerProps={{
              name: 'validFrom',
              rules: {
                required: MESSAGES.REQUIRED_VALID_FROM,
                validate: (value) =>
                  isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
              },
            }}
            handleOnChange={handleValidFromChange}
          />
        </Col>
        <Col size={6}>
          <FormControlDatePicker
            methods={methods}
            label='Valid To'
            controllerProps={{
              name: 'validTo',
              rules: {
                validate: (value) =>
                  isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
              },
            }}
            inputProps={{
              disabledDate: (current: Dayjs) =>
                validFrom ? current.isBefore(validFrom, 'day') : false,
            }}
          />
        </Col>
      </FormRow>
    </FormProvider>
  );

  if (inline) {
    return (
      <div className='parameter-inline-editor'>
        {showHeader && (
          <div className='parameter-inline-header'>
            <span>Parameter Name</span>
            <span>Parameter Value</span>
            <span>Valid From</span>
            <span>Valid To</span>
            <span>Actions</span>
          </div>
        )}
        <FormProvider {...methods}>
          <div className='parameter-inline-fields'>
            <div className='parameter-inline-cell'>
              <ParameterDefinitionControl
                methods={methods}
                parameter={parameter}
                loadingDefinitions={loadingDefinitions}
                definitionOptions={definitionOptions}
                label='Parameter Name'
                showLabel={false}
                placeholder='Select'
              />
            </div>
            <div className='parameter-inline-cell'>
              <ParameterValueControl
                methods={methods}
                valueKey={valueKey}
                listOptions={listOptions}
                showLabel={false}
              />
            </div>
            <div className='parameter-inline-cell'>
              <FormControlDatePicker
                methods={methods}
                label='Valid From'
                showLabel={false}
                controllerProps={{
                  name: 'validFrom',
                  rules: {
                    required: MESSAGES.REQUIRED_VALID_FROM,
                    validate: (value) =>
                      isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                  },
                }}
                handleOnChange={handleValidFromChange}
              />
            </div>
            <div className='parameter-inline-cell'>
              <FormControlDatePicker
                methods={methods}
                label='Valid To'
                showLabel={false}
                controllerProps={{
                  name: 'validTo',
                  rules: {
                    validate: (value) =>
                      isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                  },
                }}
                inputProps={{
                  disabledDate: (current: Dayjs) =>
                    validFrom ? current.isBefore(validFrom, 'day') : false,
                }}
              />
            </div>
          </div>
        </FormProvider>
        <div className='parameter-inline-actions'>
          <Button
            iconOnly
            icon={<Trash />}
            variant='transparent'
            size='small'
            title='Cancel parameter edit'
            onClick={handleClose}
            data-testid='btn-cancel'
          />
        </div>
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title ?? `${parameter ? 'Modify' : 'Add'} Location Parameter`}
      maskClosable={false}
      isDraggable
      width={600}
      footer={
        <div className='button-style'>
          <Button variant='outlined' onClick={handleClose} data-testid='btn-cancel'>
            Cancel
          </Button>
          <Button onClick={methods.handleSubmit(handleSubmit)} data-testid='submit-form'>
            Save
          </Button>
        </div>
      }
    >
      {formContent}
      {formError && <ErrorMessage>{formError}</ErrorMessage>}
    </Modal>
  );
};

export default AddEditLocationParameter;
